import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, coverUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PRODUCT } from "@/constants/testIds";
import Countdown from "@/components/Countdown";
import VerifiedBadge from "@/components/VerifiedBadge";
import PreviewMedia from "@/components/PreviewMedia";
import Ratings from "@/components/Ratings";
import ShareButton from "@/components/ShareButton";
import WatchButton from "@/components/WatchButton";
import AdminFeatureToggle from "@/components/AdminFeatureToggle";
import { toast } from "sonner";
import { Download, ShoppingBag, Star } from "lucide-react";

const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function readStoredRef(productId) {
  try {
    const raw = localStorage.getItem(`dd_ref_${productId}`);
    if (!raw) return null;
    const { ref, at } = JSON.parse(raw);
    if (!ref || !at || Date.now() - at > REF_TTL_MS) return null;
    return ref;
  } catch {
    return null;
  }
}

function writeStoredRef(productId, ref) {
  try {
    localStorage.setItem(`dd_ref_${productId}`, JSON.stringify({ ref, at: Date.now() }));
  } catch {
    // storage full or disabled — non-fatal
  }
}

export default function ProductDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [owned, setOwned] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  // Capture ?ref=<user_id> once when landing on a shared link.
  useEffect(() => {
    const ref = sp.get("ref");
    if (ref && /^[A-Za-z0-9_-]+$/.test(ref)) writeStoredRef(id, ref);
  }, [id, sp]);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!user) { setOwned(false); return; }
    api.get("/library").then(({ data }) => {
      setOwned(data.some((it) => it.product.id === id));
    }).catch(() => setOwned(false));
  }, [user, id]);

  const buy = async () => {
    if (!user) { nav("/login?next=/p/" + id); return; }
    setBuying(true);
    try {
      const ref_user_id = readStoredRef(id);
      const { data } = await api.post("/payments/checkout", {
        product_id: id,
        origin_url: window.location.origin,
        ref_user_id: ref_user_id || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBuying(false);
    }
  };

  const download = async () => {
    try {
      const res = await api.get(`/files/download/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = product.file_original_name || "download";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  if (loading) return <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 font-mono text-xs text-white/50">Loading…</div>;
  if (!product) return <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 font-mono text-xs text-white/50">Not found</div>;

  const soldOut = product.sold_count >= product.total_stock;
  const remaining = product.total_stock - product.sold_count;
  const ended = product.drop_ends_at && new Date(product.drop_ends_at) < new Date();
  const canRate = owned;

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-4">
          <div className="border border-white/10 bg-[#0A0A0A] overflow-hidden">
            <img src={coverUrl(product.cover_storage_path)} alt={product.title} className="w-full object-cover" />
          </div>
          <PreviewMedia product={product} />
        </div>

        <div className="lg:col-span-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CCFF00] flex items-center gap-3 flex-wrap">
            <span>// {product.category}</span>
            <span>·</span>
            <span>by{" "}
              <Link to={`/creator/${product.creator_id}`} className="text-white hover:text-[#CCFF00] underline underline-offset-2">
                {product.creator_name}
              </Link>
            </span>
            {product.creator_verified && <VerifiedBadge testId="product-verified-badge" />}
          </div>

          <h1 data-testid={PRODUCT.title} className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4 leading-[1]">
            {product.title}
          </h1>

          <div className="mt-4 flex items-center gap-3">
            {product.rating_count > 0 ? (
              <div className="inline-flex items-center gap-1.5 font-mono text-xs">
                <Star className="w-4 h-4 fill-[#CCFF00] text-[#CCFF00]" />
                <span className="text-[#CCFF00]">{product.rating_avg.toFixed(1)}</span>
                <span className="text-white/50">· {product.rating_count} review{product.rating_count === 1 ? "" : "s"}</span>
              </div>
            ) : (
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">No reviews yet</div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <div data-testid={PRODUCT.price} className="font-mono font-bold text-3xl text-[#CCFF00]">
              ${product.price.toFixed(2)}
            </div>
            <div data-testid={PRODUCT.stockCounter} className="font-mono text-xs uppercase tracking-[0.2em] text-white/70 border border-white/20 px-3 py-1.5">
              {product.sold_count}/{product.total_stock} SOLD · {remaining} LEFT
            </div>
            {product.drop_ends_at && <Countdown endsAt={product.drop_ends_at} />}
            <ShareButton
              testId={`${PRODUCT.shareBtn}-hero`}
              variant="hero"
              url={typeof window !== "undefined" ? `${window.location.origin}/p/${product.id}` : `/p/${product.id}`}
              shareUrl={typeof window !== "undefined" ? `${window.location.origin}/api/share/product/${product.id}` : `/api/share/product/${product.id}`}
              title={`${product.title} · by ${product.creator_name}`}
              description={product.description}
            />
          </div>

          <p className="mt-8 text-white/80 leading-relaxed whitespace-pre-wrap">{product.description}</p>

          {product.tags && product.tags.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {product.tags.map((t) => (
                <Link
                  key={t}
                  to={`/?tags=${encodeURIComponent(t)}`}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] border border-white/15 hover:border-white/40 px-2 py-1 text-white/80"
                >
                  #{t}
                </Link>
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-3 items-center">
            {owned ? (
              <button
                data-testid={PRODUCT.downloadBtn}
                onClick={download}
                className="inline-flex items-center gap-2 bg-[#CCFF00] text-black font-mono uppercase tracking-[0.2em] text-xs px-5 py-3 hover:opacity-90"
              >
                <Download className="w-4 h-4" /> Download
              </button>
            ) : (
              <button
                data-testid={PRODUCT.buyBtn}
                onClick={buy}
                disabled={soldOut || ended || buying}
                className="inline-flex items-center gap-2 bg-[#FF3366] text-white font-mono uppercase tracking-[0.2em] text-xs px-5 py-3 hover:bg-[#FF3366]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:scale-[1.02]"
              >
                <ShoppingBag className="w-4 h-4" />
                {soldOut ? "Sold out" : ended ? "Drop ended" : buying ? "Redirecting…" : `Buy · $${product.price.toFixed(2)}`}
              </button>
            )}
            {(soldOut || ended) && !owned && (
              <WatchButton productId={product.id} testId="product-watch-btn" />
            )}
            <ShareButton
              testId={PRODUCT.shareBtn}
              url={typeof window !== "undefined" ? `${window.location.origin}/p/${product.id}` : `/p/${product.id}`}
              shareUrl={typeof window !== "undefined" ? `${window.location.origin}/api/share/product/${product.id}` : `/api/share/product/${product.id}`}
              title={`${product.title} · by ${product.creator_name}`}
              description={product.description}
            />
            <AdminFeatureToggle productId={product.id} />
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 text-center">
            <Stat label="Format" value={(product.file_mime.split("/")[1] || "file").toUpperCase()} />
            <Stat label="Size" value={fmtSize(product.file_size)} />
            <Stat label="Category" value={product.category.toUpperCase()} />
          </div>
        </div>
      </div>

      <Ratings productId={product.id} canRate={canRate} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 py-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="font-mono text-sm mt-1">{value}</div>
    </div>
  );
}

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024*1024) return `${(n/1024).toFixed(1)} KB`;
  if (n < 1024*1024*1024) return `${(n/1024/1024).toFixed(1)} MB`;
  return `${(n/1024/1024/1024).toFixed(2)} GB`;
}
